import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'
import { useWalletsWriteContext } from '@context/UserWalletContext'

import {
  getDocumentVerificationPath,
  isDepositBeforeKYC,
  isSumsubNeeded,
} from '@utils/AccountAccess/accountKYCStatuses'
import { CommonPaths, useBasePath } from '@utils/path'

import { isVerificationExpedited } from '@model/AccountDetailedDto'
import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { WalletSetupIndicator } from './WalletSetupIndicator'

const usePostWalletSetupNavigation = () => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const { refreshUserWallets } = useWalletsWriteContext()
  const navigate = useNavigate()
  const locale = useSessionLanguage()
  const { refreshAccount } = useAccountWriteContext()
  const { getBasePath } = useBasePath(true)

  const [isWalletInitialized, setWalletInitialized] = useState(false)
  const [walletsFetching, setWalletsFetching] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setWalletInitialized(true), 2000)

    return () => clearTimeout(timer)
  }, [])

  const handleClickNext = async () => {
    try {
      setWalletsFetching(true)

      const wallets = await refreshUserWallets()
      const newAccount = await refreshAccount(locale)

      if (!newAccount) {
        return
      }

      if (
        !isDepositBeforeKYC(newAccount) &&
        isVerificationExpedited(newAccount) &&
        !isAccountActivatedStatus(newAccount)
      ) {
        refreshAccount(locale)
        navigate(getDocumentVerificationPath(newAccount))

        return
      }

      if (!isDepositBeforeKYC(newAccount) && isSumsubNeeded(newAccount)) {
        const path = await getBasePath(newAccount)

        refreshAccount(locale)
        navigate(path)

        return
      }

      if (isWalletInitialized && (!wallets || wallets.length === 0)) {
        addSnackbar.error({ message: t('errors.Something went wrong') })
        return
      }

      refreshAccount(locale)
      navigate(CommonPaths.Dashboard, { state: { wallets } })
    } finally {
      setWalletsFetching(false)
    }
  }

  return {
    isWalletInitialized,
    walletsFetching,
    handleClickNext,
  }
}

export const WalletSetupLoader: React.FC = () => {
  useScrollToTop()

  const { handleClickNext, isWalletInitialized, walletsFetching } = usePostWalletSetupNavigation()

  return (
    <WalletSetupIndicator
      onSetupComplete={handleClickNext}
      isButtonDisabled={!isWalletInitialized || walletsFetching}
    />
  )
}
